<template>
    <div>
        <h3>本文を執筆する</h3>
        <div>
            <p>
                「本文追加」ボタンから本文を新規追加するか、追加済みの本文の「編集」ボタンをクリックすることで、
                本文の編集画面を開きます。
            </p>
            <p>
                貸し本棚の本文編集機能は、作者が小説の執筆に集中できるようにデザインされています。
                本文の執筆を開始すると、執筆画面は本文とタイトルだけになり、余計なボタンやリンクを表示しません。
            </p>

            <h4>自動保存</h4>
            <img src="/img/help/novel/04.png" alt="自動保存とライブ配信" class="img-thumbnail">
            <p>
                記述中の本文を自動保存します。
                未公開本文の場合は自動的にONになります。
                公開済みの本文の場合は、自動保存はOFFの状態になります。
                公開済み本文の自動保存をONにする場合は、執筆中に閲覧している読者が、
                執筆途中の本文を目にする可能性があります。
            </p>
            <h5>新規本文の自動保存</h5>
            <p>
                まだ保存されていない（未確定の）新規本文でも自動保存は有効です。
                このとき、内部ではその小説の新規本文として仮保存されています。
                誤って新規本文を保存する前に編集画面を離脱した場合でも、新規本文を再度追加することで、
                それまで記述した本文が復帰します。<br>
                この自動保存情報は、ユーザーがログアウトすると消滅します。
            </p>

            <h4>ライブ配信</h4>
            <p>
                ONにすると、ブラウザの新規タブに執筆のライブ画面を表示します。
                このライブ画面は、執筆中の本文をほぼリアルタイムに配信します。
                ライブ画面はURLさえ知っていれば、誰でもアクセスできます。<br>
                ライブ配信中は目次から別のエピソードに切り替えることができません。
            </p>
            <h5>実画面の確認用</h5>
            <p>
                ライブ機能の使い方として最も多い方法が、PCによる実画面の確認用途です。
                執筆中の画面と別にライブ画面を表示し、左右に並べたり、別モニタに表示させることで、
                縦書き表示などの実画面表示で執筆中の本文を確認することができます。
            </p>
            <p>
                確認時にページの最下部（縦書き時は左端）にスクロールしておくと、行が増えたときに自動スクロールします。
            </p>
            <h5>執筆ライブ用</h5>
            <p>
                ライブ画面のURLをSNSなどで告知することにより、熱心な読者のために執筆をライブ配信することができます。
                ライブ画面は開始と終了の時刻が表示されます。
                作者は何人閲覧しているかはわからないようになっています。
            </p>

            <h4>エディタ</h4>
            <p>
                エディタはタイトル入力と本文の入力に分かれています。
                その上に表示されるルビや傍点などの機能は、どちらの入力にも有効です。
                但し、タイトルにルビや傍点を打つと、貸し本棚では正常に表示されますが、
                他の投稿サイトに転載すると、正常に表示されない可能性があります。
            </p>
            <img src="/img/help/novel/05.png" alt="本文エディタ" class="img-thumbnail mb-3">
            <h5>本文のタイトル</h5>
            <p>
                空欄にもできますが、空欄の場合「（無題）」と表記されます。
            </p>
            <h5>本文</h5>
            <p>
                本文を入力します。
            </p>
            <p>
                本文は１話につき10万字まで記述できますが、6万字を超えると画面が重くなります。
                <b>小説作品に文字数制限はありません。</b>
                本文入力には様々な補助機能があります。
            </p>
            <h6>ルビ（Ctrl+1）</h6>
            <p>
                ルビを打ちたい部分を選択して、ルビメニューをクリックするか、PCの場合はCtrl+1でルビタグを入力します。
                ルビは既定では<br>
            </p>
            <blockquote>
                引出しをあけて｜掻《か》きまわし<br>
                ↓<br>
                引出しをあけて<ruby>掻<rt>か</rt></ruby>きまわし<br>
            </blockquote>
            <p>
                のように変換されます。
                「|」はルビ範囲の指定子ですが、未指定の場合は()の前の漢字部分が自動選択されます。<br>
            </p>
            <h6>傍点（Ctrl+2）</h6>
            <p>
                傍点を打ちたい部分を選択して、傍点メニューをクリックするか、PCの場合はCtrl+2で傍点タグを入力します。
                傍点は二重の《《》》で指定できます。<br>
            </p>
            <blockquote>
                国中の《《つむ》》を焼き捨てました。<br>
                ↓<br>
                国中の<em>つむ</em>を焼き捨てました。<br>
            </blockquote>
            <h6>合字</h6>
            <p>
                縦書き時に一部の半角英数字は自動的に縦書きになります。
            </p>
            <ul>
                <li>４文字以内の英字、半角スペース。</li>
                <li>３文字以内の数字、エクスクラメーション（!）、クエスチョン（?）などの、アルファベットを含まない半角文字。</li>
            </ul>
            <p>
                上記条件に当てはまる場合は縦書き、それ以外は横書きになります。
            </p>
            <h6>字下げ</h6>
            <p>
                全角スペース２〜９文字で始まる行は、スペースの数だけ字下げされます。
                字下げされた行は、改行されても字下げされます。
                スマートフォンなどのレイアウトにおいて、意図したとおりに表示されます。
            </p>
            <h6>地付き・地寄せ</h6>
            <p>
                全角スペース１０文字以上で始まる行は、地付きとなります。
                横書きでは右端に、縦書きでは最下部にレイアウトされます。
                地寄せ機能はありません。
                地付きの文末に全角スペースを打つことで、地からスペース分上げて地寄せ表現が可能です。
            </p>
            <h5>プレビュー</h5>
            <img src="/img/help/novel/06.png" alt="プレビュー" class="img-thumbnail">
            <p>
                ルビや傍点が変換された状態をプレビューできます。
                <i class="fas fa-eye"></i>アイコンをクリックしてください。
                編集中の本文がプレビューモードに切り替わります。
                再度ボタンをクリックするか、本文をクリックすることで編集モードに戻ります。
            </p>
            <h5>実画面プレビュー</h5>
            <p>
                実際の本文閲覧画面に近いプレビューです（完全に同一ではありません）。
                縦書き等のレイアウトの変更も可能です。
            </p>
            <h5>保存（Ctrl+S/⌘+S）</h5>
            <p>
                三点リーダ（<i class="fas fa-ellipsis-v"></i>）から開けるメニューに、「保存」メニューがあります。
                このメニューから現在の本文を上書き保存することができます。
                PCの場合は、編集しながらCtrl+Sあるいは⌘+Sのショートカットで保存することも可能です。
            </p>
            <h5>ルビ削除</h5>
            <p>
                本文に含まれるすべてのルビを削除します。
                傍点は削除しません。
            </p>


            <h4>検索と置換</h4>
            <img src="/img/help/novel/07.jpg" alt="検索と置換" class="img-thumbnail mb-3">
            <p>
                <i class="fas fa-search"></i>アイコンをクリックすると、
                検索と置換のウインドウが開きます。
                この検索は、<strong>現在編集中の本文だけを検索することもできますが、その小説のすべての本文を対象に横断検索を行うことも可能です。</strong>
            </p>
            <h5>検索</h5>
            <p>
                検索文字列を入力してEnterキーを押すか、「この小説を検索」ボタンをクリックすると、
                編集中の小説全体から指定文字列を検索し、検索結果がウインドウ上にリストされます。
                Ctrl+Enter（⌘+Enter）か、「この本文を検索」ボタンをクリックすると、
                編集中の本文のみを対象に指定文字列を検索します。
                この検索結果をクリックすると、該当の本文に遷移できます。
                検索結果は保持されるので、本文を編集している限り、結果が消えることはありません。
            </p>
            <h5>置換</h5>
            <p>
                検索文字列を指定して文字列に一括置換することもできます。
                「この本文を置換」をクリックすると、編集中の本文のみを対象に指定文字列に置換を行います。
                「この小説を置換」をクリックすると、編集中の小説全体を対象に指定文字列に置換を行います。
                検索時に「この本文を検索」を行っている場合「この小説を置換」は実行できません。
            </p>
            <h5>元に戻す</h5>
            <p>
                一括置換を行うと、置換する前の履歴がユーザーのブラウザに保存されます。
                最大 <strong>{{$store.state.max_histories}}</strong> 件まで保存され、それを超える履歴を保存すると、古いものから削除されていきます。
                ブラウザに保存される履歴は可逆暗号化され、サーバ内でのみ暗号化と復号化が行われます。
                第三者に内容を読まれることはありません。
            </p>
            <p>
                置換履歴から置換した内容を元に戻すことができます。
                置換履歴の復元は、指定した時点に全ての本文を元に戻す処理となります。
                置換の後に編集した内容は全て失われます。
                原則として、置換の復元は置換処理中に間違いに気付いた時に実行してください。
                置換処理後に執筆を再開し、しばらく間をおいてから復元を行うのは危険です。
            </p>
            <div class="table-responsive">
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>第1話</th>
                            <th>第2話</th>
                            <th>第3話</th>
                            <th>処理</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <span class="badge badge-primary">田中→鈴木</span>
                            </td>
                            <td></td>
                            <td>
                                <span class="badge badge-primary">田中→鈴木</span>
                            </td>
                            <td>
                                <span class="badge badge-primary">田中→鈴木</span>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span class="badge badge-primary">田中→鈴木</span>
                            </td>
                            <td>
                                <span class="badge badge-success">親和性→相性</span>
                            </td>
                            <td>
                                <span class="badge badge-primary">田中→鈴木</span>
                                <span class="badge badge-success">親和性→相性</span>
                            </td>
                            <td>
                                <span class="badge badge-success">親和性→相性</span>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span class="badge badge-primary">田中→鈴木</span>
                            </td>
                            <td>
                                <span class="badge badge-success">親和性→相性</span>
                                <span class="badge badge-warning">レコード→レコオド</span>
                            </td>
                            <td>
                                <span class="badge badge-primary">田中→鈴木</span>
                                <span class="badge badge-success">親和性→相性</span>
                                <span class="badge badge-warning">レコード→レコオド</span>
                            </td>
                            <td>
                                <span class="badge badge-warning">レコード→レコオド</span>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span class="badge badge-primary">田中→鈴木</span>
                            </td>
                            <td>
                            </td>
                            <td>
                                <span class="badge badge-primary">田中→鈴木</span>
                            </td>
                            <td>
                                <span class="badge badge-success">親和性→相性</span>
                                を元に戻す
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h4>本文を分解する</h4>
            <p>
                長過ぎる本文をキリの良いところで２話に分解したいとき、指定箇所で分解する機能があります。
            </p>
            <img src="/img/help/novel/08.png" alt="本文を分解する" class="img-thumbnail mb-3">
            <h5>カーソル位置で分解...</h5>
            <p>
                三点リーダ（<i class="fas fa-ellipsis-v"></i>）から開けるメニューに、
                「カーソル位置で分解...」があります。
                これは、本文を分解したい位置にカーソルを移動して、このメニューを選択すると、
                カーソルから前の本文、後の本文で２話に分解するウインドウが開きます。
                このウインドウで、後編のタイトルを指定して「本文を分解する」をクリックすると、
                本文を２話に分解します。
            </p>
            <p>
                万が一、誤って本文を分解してしまった場合、分解した２話の本文を１話に結合する機能で復元できます。
                また、タイトルに第n話のようなナンバリングが入っている場合、タイトル正規化機能で、ナンバリングを修正することができます。
            </p>
            <h5>版や一括置換を元に戻す場合</h5>
            <p>
                一括置換や版管理で保存した編集履歴を分解・置換前に戻す場合、分解された後のエピソードは新規エピソードとなるため、
                編集履歴が復元しません。
                この問題を回避するには、分解したエピソードを糾合して元に戻します。
            </p>
            <div class="table-responsive">
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>エピソード</th>
                            <th>処理</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <span class="badge badge-primary">第１話 1,000文字</span>
                            </td>
                            <td>
                                版を保存/一括置換
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span class="badge badge-success">第１話-1 500文字</span>
                                <span class="badge badge-warning">第１話-2 500文字</span>
                            </td>
                            <td>
                                分解
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span class="badge badge-primary">第１話 1,000文字</span>
                                <span class="badge badge-warning">第１話-2  500文字</span>
                            </td>
                            <td>
                                復元→第１話-2が重複した内容となる
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h4>本文を糾合する</h4>
            <p>
                短すぎる本文どうしをひとつにまとめることができます。
            </p>
            <img src="/img/help/novel/09.png" alt="本文を糾合する" class="img-thumbnail mb-3">
            <h5>次の本文と糾合...</h5>
            <p>
                三点リーダ（<i class="fas fa-ellipsis-v"></i>）から開けるメニューに、
                「次の本文と糾合...」があります。
                このメニューを選択すると、本文糾合ウインドウが開き、
                現在の本文の最後と、次の本文の最初の部分が確認表示されます。
                「本文を糾合する」をクリックすると、２話が１話に糾合されます。
                このとき、結合される「次の本文」のタイトルは消滅します。
            </p>
            <h5>版や一括置換を元に戻す場合</h5>
            <p>
                <strong>版や一括置換の履歴によって本文を元に戻す場合、事前に糾合を行っている場合はご注意ください。</strong>
                糾合処理を実行後、糾合先のエピソードを糾合前に戻してしまうと、糾合されたエピソードの文章は
                <span class="text-danger">完全に失われてしまいます。</span>
                糾合されたエピソードの本文は糾合先に追加され、糾合されたエピソード自体は削除されます。
            </p>
            <div class="table-responsive">
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>エピソード</th>
                            <th>処理</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <span class="badge badge-primary">第１話 1,000文字</span>
                                <span class="badge badge-success">第２話 1,000文字</span>
                            </td>
                            <td>
                                第１話の版を保存
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span class="badge badge-info">第１話 2,000文字</span>
                            </td>
                            <td>
                                第１話に第２話を糾合
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span class="badge badge-primary">第１話 1,000文字</span>
                            </td>
                            <td>
                                第１話の版を復元→第２話が消失する
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h4>本文の横断編集</h4>
            <p>
                本文の編集中、以前のエピソードに遡って編集し、また最新のエピソードに戻って編集する、
                という作業のために、逐一本文一覧画面に戻るのは手間がかかります。<br>
                エディタ右上にある目次アイコン <i class="fas fa-list"></i> をクリックすると、
                本文の目次が表示されます。ここでクリックした本文へ画面を切り替えることなく遷移することができます。
            </p>
            <img src="/img/help/novel/10.png" alt="目次" class="img-thumbnail mb-3">
            <p>
                注意点すべき点として、
                <b>一度でも公開中の本文を表示すると、自動保存が切れます。</b>
                公開中の本文において自動保存は既定でOFFとなっています。
            </p>

            <h4>未保存時の遷移について</h4>
            <img src="/img/help/novel/18.png" alt="目次" class="img-thumbnail mb-3">
            <p>
                本文が未保存のときに、別の本文や本文一覧画面などに遷移しようとしたとき、
                未保存の警告ウインドウが開きます。
                ここで任意に「離れる」をクリックすると、本文を保存せずに離脱することができます。
            </p>
            <p>
                アクティビティアイコン（<i class="far fa-bell"></i>）で開くドロップダウンメニューから、
                更新された小説やお知らせに遷移しようとすると、これはページの完全な離脱が実行されるため、
                警告ウインドウが表示されません。
            </p>
            <p>
                ここに警告ウインドウを表示してしまうと、「ブラウザを閉じる」という動作においても警告が表示され、
                ユーザーの意思を妨げる処理となります。
                貸し本棚では、貸し本棚自体の操作ではない挙動を制限しないため、ここに警告ウインドウを表示しない仕様になっています。
                失っては困る本文の編集には「自動保存」をご活用ください。
            </p>

            <h4>挿絵について</h4>
            <p>
                現状、小説家になろう、ミッドナイトノベルズ、ノクターンノベルズ、ムーンライトノベルズで使用できる
                「みてみん」及び「えぱれっと」形式のタグを画像タグに展開します。
            </p>
            <p>
                これは上記投稿サービスのために執筆された原稿を執筆した際、
                みてみん形式のタグがそのまま展開されてしまうことを防ぐためであり、
                貸し本棚への投稿小説のために、みてみんを利用することを許可、推奨するものではありません。
            </p>
            <h5>使い方</h5>
            <ol>
                <li>「<a href="https://mitemin.net/" target="_blank">みてみん</a>」あるいは
                「<a href="https://eparet.net/" target="_blank">えぱれっと</a>」に挿絵をアップロードします。</li>
                <li>
                    挿絵の画像ページのURLをコピーします。このような形式です：
                    <code>https://40811.mitemin.net/i707809/</code>
                </li>
                <li>本文にURLを貼り付けます。貼り付けると小説家になろうタグ（<code>&lt;i707809|40811&gt;</code>）に自動変換します。</li>
            </ol>
            <p>
                上記手順で簡単に画像タグを貼り付けることができ、公開本文に挿絵が表示されます。
                この本文はそのまま「小説家になろう」などに貼り付けることで、同様に挿絵を表示できます。
            </p>
        </div>
    </div>
</template>

<style src="../help.scss" scoped lang="scss"></style>

<script>
export default {
    created() {
        this.useHead({
            title: "本文を記述する | 貸し本棚ヘルプ",
            link: [
                {
                    rel: "canonical",
                    href: location.origin + location.pathname,
                    id: "canonical"
                }
            ]
        });
    }
}
</script>